<script setup lang="ts">
import { computed } from 'vue'
import SwordTagWuk from '@ui-kit/components/tag/Tag.vue'
import { useSlotText } from '@/composables/useSlotText'

// Bridge component between legacy Vue 2 UI (web-ui-kit) and new Vue 3 UI (@swordhealth/ui)
// Accepts props compatible with @swordhealth/ui Tag component (https://github.com/SWORDHealth/ui/blob/master/packages/ui/src/components/tag/Tag.vue)
// But internally uses the legacy web-ui-kit Tag component (https://github.com/SWORDHealth/web-ui-kit/blob/master/src/components/tag/Tag.vue)

// Props mapping from new UI to legacy UI:
// - size: 'sm' → 'small', 'md' → 'medium', 'lg' → 'large'
// - color: accepts same values (black, white, neutral, blue, green, grey, red, yellow) + 'blue-red' for gradient
// - icon → iconLeft (new UI uses 'icon', legacy uses 'iconLeft')
// - iconRight → iconRight (both use same prop name)
// - removable → removable (both use same prop name)
// - disabled → disabled (both use same prop name)
// - text → extracted from default slot content and passed as 'text' prop to legacy component

interface XTagProps {
  // @swordhealth/ui compatible props (Vue 3)
  size?: 'sm' | 'md' | 'lg'
  color?: 'black' | 'white' | 'neutral' | 'blue' | 'green' | 'grey' | 'red' | 'yellow' | 'gradient' | 'primary' | 'secondary'
  icon?: string
  iconRight?: string
  removable?: boolean
  disabled?: boolean
  rounded?: boolean // New UI prop (maps to radiusType='full')
  semiBold?: boolean
  outlined?: boolean // New UI prop (maps to withStroke)
  removeLabel?: string // New UI prop for accessibility
  tag?: string // HTML tag (default 'span')
}

const props = withDefaults(defineProps<XTagProps>(), {
  size: 'md',
  color: 'blue',
  tag: 'span',
})

const text = useSlotText()

const legacySize = computed(() => {
  const sizeMap = { 'sm': 'small', 'md': 'medium', 'lg': 'large' }

  return sizeMap[props.size] || 'medium'
})

const legacyColor = computed(() => {
  if (props.color === 'gradient') return 'blue-red'
  if (props.color === 'primary') return 'blue'
  if (props.color === 'secondary') return 'yellow'

  return props.color
})

const emit = defineEmits<{
  (e: 'remove', event: Event): void
  (e: 'click', event: MouseEvent): void
}>()

function handleRemove(event: Event) {
  emit('remove', event)
}

function handleClick(event: MouseEvent) {
  emit('click', event)
}
</script>

<template>
  <sword-tag-wuk
    :style="{'--text-display': !text && 'none'}"
    v-bind="$attrs"
    :text="text || ''"
    :size="legacySize"
    :color="legacyColor"
    :icon-left="icon"
    :icon-right="iconRight"
    :removable="removable"
    :disabled="disabled"
    :semi-bold="semiBold"
    :radius-type="rounded ? 'full' : 'small'"
    :with-stroke="outlined"
    @click.native="handleClick"
    @remove="handleRemove"
  />
</template>

<style lang="scss" scoped>
::v-deep {
  .tag__text {
    display: var(--text-display);
  }
}
</style>
